import type { TranslationResource } from "../en";
import { common } from "./common";
import { nav } from "./nav";
import { footer } from "./footer";
import { auth } from "./auth";
import { gamification } from "./gamification";
import { home } from "./home";
import { settings } from "./settings";
import { pages } from "./pages";

/**
 * A language that ships while translation is still in progress.
 *
 * Vietnamese is the second language, so its file is allowed to be *incomplete*:
 * a key that has not been translated yet falls back to English at runtime
 * (i18next is configured with `fallbackLng: "en"`). The alternative — requiring
 * every key up front — means a single missing translation blocks the whole
 * locale from shipping, which is how locales rot.
 *
 * What is *not* allowed is a key English doesn't have. `DeepPartial` enforces
 * that direction: excess-property checking makes an unknown key in a
 * translation a type error, so a typo can't hide a string forever.
 *
 * TypeScript catches unknown and mis-shaped keys but cannot catch a *missing*
 * one, because missing is legal here. `scripts/i18n-coverage.mjs` counts those,
 * and `npm run test:i18n` asserts the invariants TypeScript cannot.
 */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends readonly string[]
    ? // An array of strings (a list of steps, a set of labels). Widened to
      // `string[]` because the English source is `as const`, which would
      // otherwise pin every translated element to the English literal type and
      // reject every non-English string.
      readonly string[]
    : T[K] extends string
      ? string
      : T[K] extends object
        ? DeepPartial<T[K]>
        : T[K];
};

export type PartialTranslation = DeepPartial<TranslationResource>;

/**
 * Vietnamese.
 *
 * Terminology is fixed once here and reused everywhere:
 *   - streak       → chuỗi ngày (lit. "chain of days"). Never left as "streak".
 *   - topic        → chủ đề
 *   - vocabulary   → từ vựng
 *   - review (SRS) → ôn tập
 *   - practice     → luyện tập
 *   - deck         → bộ thẻ
 *
 * Register: `bạn` (friendly, 2nd person singular) addresses the learner — the
 * same register the English copy uses with "you".
 *
 * The English *learning* content is never translated. Only chrome around it.
 * Split into per-domain files to mirror `../en`, which lets the coverage script
 * compare namespaces file-for-file.
 */
export const vi: PartialTranslation = {
  common,
  nav,
  footer,
  auth,
  gamification,
  home,
  settings,
  pages,
};

export default vi;
